import { useCallback } from 'react';
import { SECURE_BASE_URL } from '../../config';
import { tryToIdentify } from '../../redux/actions/analytics';
import { setAuthConfig } from '../../redux/actions/authConfig';
import { UserInfoAPIResponse } from '../../types/auth';
import { authRequest } from '../../utils/auth-request';
import { AUTHENTICATE_NESTED, MY_TV } from '../../utils/router';
import useInAppNavigate from '../router/use-in-app-navigate';
import useAppDispatch from '../use-app-dispatch';
import useAuthRequestCallbacks from './use-auth-request-callbacks';
import useGetRegisteredUserAuthCode from './use-get-registered-user-auth-code';
import useRedirectAfterAuthFlowComplete from './use-redirect-after-auth-flow-complete';

export const LOGIN_STATUS_URL = `${SECURE_BASE_URL}/auth/info/login_code`;

/** KU
 *
 * Returns a handler, {@link handleCheckLoginStatus}, which checks if the user
 * is authenticated. If they are, {@link handleCheckLoginStatus} will redirect
 * the user to `player`. If they aren't, {@link handleCheckLoginStatus} will
 * either redirect the user to `/authenticate` so they can begin the sign-in
 * process or automatically request an auth code and send the user to
 * `/authorize` (`tryToLogIn=true` case). If the user isn't authenticated,
 * {@link handleCheckLoginStatus} also sets the `authConfig.login*URLs`.
 */
export default function useCheckLoginStatus() {
  const { onError } = useAuthRequestCallbacks();
  const { handleGetRegisteredUserAuthCode } = useGetRegisteredUserAuthCode();
  const { handleRedirectAfterAuthFlowComplete } = useRedirectAfterAuthFlowComplete();

  const navigate = useInAppNavigate();
  const dispatch = useAppDispatch();

  const handleCheckLoginStatus = useCallback((tryToLogIn = false) => {
    const onAuthSuccess = ({ analytics }: UserInfoAPIResponse) => {
      dispatch(tryToIdentify(analytics));
      handleRedirectAfterAuthFlowComplete(MY_TV);
    };
    const onAuthFailure = ({ poll, action, init }: UserInfoAPIResponse) => {
      dispatch(
        setAuthConfig({
          loginPollURL: poll,
          loginActionURL: action,
          loginInitURL: init,
        }),
      );
      if (tryToLogIn) {
        // MM: if we aren't navigating to authenticate we need to send the
        // auth code to the user for the authorize screen
        // Firing this action here since we know we have the loginInitURL
        handleGetRegisteredUserAuthCode();
      } else {
        navigate(AUTHENTICATE_NESTED, { replace: true });
      }
    };

    authRequest({
      url: LOGIN_STATUS_URL,
      onAuthSuccess,
      onAuthFailure,
      onError,
    });
  }, []);

  return { handleCheckLoginStatus };
}
